<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $t('messages.advance_school_year'),
                        href: null,
                    },
                    {
                        name: '-',
                        href: null,
                    },
                ]"
            />
        </template>

        <form action="/advance-students" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.school_year') }} -
                    {{ nextSchoolYear.year_range }}
                </template>
                <div class="md:grid md:grid-cols-6 md:gap-6">
                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{
                                $t('messages.start_of_X_trimester', {
                                    number: 1,
                                })
                            }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="date"
                                v-model="form.nextSchoolYear.first_trimester_start_date"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>

                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.end_of_X_trimester', { number: 1 }) }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="date"
                                v-model="form.nextSchoolYear.first_trimester_end_date"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>

                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{
                                $t('messages.start_of_X_trimester', {
                                    number: 2,
                                })
                            }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="date"
                                v-model="form.nextSchoolYear.second_trimester_start_date"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>

                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.end_of_X_trimester', { number: 2 }) }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="date"
                                v-model="form.nextSchoolYear.second_trimester_end_date"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>

                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{
                                $t('messages.start_of_X_trimester', {
                                    number: 3,
                                })
                            }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="date"
                                v-model="form.nextSchoolYear.third_trimester_start_date"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>

                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.end_of_X_trimester', { number: 3 }) }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="date"
                                v-model="form.nextSchoolYear.third_trimester_end_date"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div v-for="group in form.groups" :key="group.id">
                <div class="mt-5">
                    <template v-for="(groupFromList, index) in groups" :key="groupFromList.id">
                        <span v-if="index !== 0">-</span>
                        <span v-if="groupFromList.name === group.name" :id="group.name">
                            {{ group.name }}
                        </span>
                        <span v-else class="text-gray-400">
                            <a :href="`#${groupFromList.name}`">
                                {{ groupFromList.name }}
                            </a>
                        </span>
                    </template>
                </div>

                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="table-fixed min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="w-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.name') }}
                                        </th>
                                        <th
                                            class="w-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.state') }}
                                        </th>
                                        <th
                                            class="w-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.grade_level') }}
                                        </th>
                                        <th
                                            class="w-1/4 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.letter') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(student, index) in group.students">
                                        <tr :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                {{ student.full_name }}
                                                <BadgePill v-if="student.repeats">
                                                    {{ $t('messages.repeats_grade') }}
                                                </BadgePill>
                                            </td>
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <select
                                                    v-model="student.status"
                                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                >
                                                    <option
                                                        v-for="status in ['continue', 'inactive']"
                                                        :value="status"
                                                        :key="status"
                                                    >
                                                        {{ $t('messages.' + status) }}
                                                    </option>
                                                </select>
                                            </td>
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <select
                                                    :disabled="student.status === 'inactive'"
                                                    v-model="student.group.grade_level_id"
                                                    :required="student.status !== 'inactive'"
                                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                >
                                                    <option :value="null">-</option>
                                                    <option v-for="gradeLevel in gradeLevels" :value="gradeLevel.id">
                                                        {{ gradeLevel.name }}
                                                    </option>
                                                </select>
                                            </td>
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <select
                                                    v-model="student.group.letter"
                                                    :disabled="student.status === 'inactive'"
                                                    :required="student.group.grade_level_id !== null"
                                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                >
                                                    <option
                                                        v-for="letter in ['A', 'B', 'C', 'D', 'E', 'F', 'G']"
                                                        :value="letter"
                                                    >
                                                        {{ letter }}
                                                    </option>
                                                </select>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        BadgePill,
        TitleFieldsFormSection,
    },
    props: ['groups', 'nextSchoolYear', 'gradeLevels'],
    layout: Layout,
    data() {
        return {
            form: this.$inertia.form({
                nextSchoolYear: this.nextSchoolYear,
                groups: this.groups,
            }),
        };
    },
    name: 'Edit',
    methods: {
        submit() {
            this.form.patch('/advance-students');
        },
    },
};
</script>
